// Browser → raw ESC/P printing via QZ Tray (a small local app installed on the
// branch PC). QZ Tray runs a localhost WebSocket; we connect, then send the
// ESC/P byte stream with RAW datatype so the printer uses its own font ROM.
//
// Unsigned/community mode: we don't set certificate/signature promises, so QZ
// Tray shows a one-time "Allow / Remember" prompt per site — acceptable for an
// internal branch tool.
import qz from "qz-tray";

export const QZ_DOWNLOAD_URL = "https://qz.io/download/";

// Uint8Array → base64 (chunked to avoid call-stack limits on large arrays).
function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function isQzConnected() {
  try {
    return qz.websocket.isActive();
  } catch (e) {
    return false;
  }
}

// Connect to the local QZ Tray. Rejects if QZ Tray isn't running.
export async function connectQz() {
  if (isQzConnected()) return true;
  await qz.websocket.connect();
  return true;
}

export async function listPrinters() {
  await connectQz();
  const printers = await qz.printers.find();
  return Array.isArray(printers) ? printers : [printers];
}

// Best-guess default printer name. Prefer a raw/text-only queue (needed on
// Windows), then an LX/EPSON queue.
export function guessLxPrinter(printers) {
  const lower = (s) => String(s).toLowerCase();
  return (
    printers.find((p) => /raw|text only|generic\s*\/\s*text/.test(lower(p))) ||
    printers.find((p) => /lx.?310/.test(lower(p))) ||
    printers.find((p) => lower(p).includes("lx")) ||
    printers.find((p) => lower(p).includes("epson")) ||
    null
  );
}

// Send a raw ESC/P byte stream to the given printer. We print THROUGH the
// spooler (RAW datatype) — NOT forceRaw: forceRaw uses a direct-to-device
// method that is "not supported on Windows", which makes the job appear sent
// yet nothing prints. The normal spooler raw path works for ESC/P printers.
export async function printRawEscp(bytes, printerName) {
  if (!printerName) throw new Error("No printer selected");
  await connectQz();
  const config = qz.configs.create(printerName);
  const data = [
    { type: "raw", format: "command", flavor: "base64", data: bytesToBase64(bytes) },
  ];
  await qz.print(config, data);
  return true;
}
